// API service configuration for KATA Dashboard

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { AuthTokens } from '../types/auth';

// API configuration
const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:8400/api';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000,
});

// Token management
let authTokens: AuthTokens | null = null;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Cookie utilities for secure token storage
const setCookie = (name: string, value: string, days: number = 7) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict; Secure=${location.protocol === 'https:'}`;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const setAuthTokens = (tokens: AuthTokens | null) => {
  authTokens = tokens;
  if (tokens) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

    // Store in both localStorage (for compatibility) and cookies (more secure)
    localStorage.setItem('kata_auth_tokens', JSON.stringify(tokens));
    setCookie('kata_access_token', tokens.access, 1); // 1 day for access token
    setCookie('kata_refresh_token', tokens.refresh, 7); // 7 days for refresh token
  } else {
    delete apiClient.defaults.headers.common['Authorization'];

    // Clear from both storage methods
    localStorage.removeItem('kata_auth_tokens');
    deleteCookie('kata_access_token');
    deleteCookie('kata_refresh_token');

    // Also clear cached user data when tokens are cleared
    localStorage.removeItem('kata_user_data');
  }
};

// User data caching functions
export const setCachedUser = (user: any) => {
  localStorage.setItem('kata_user_data', JSON.stringify({
    user,
    timestamp: Date.now()
  }));
};

export const getCachedUser = (): any | null => {
  try {
    const cached = localStorage.getItem('kata_user_data');
    if (!cached) return null;

    const { user, timestamp } = JSON.parse(cached);
    // Cache is valid for 24 hours
    if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem('kata_user_data');
      return null;
    }

    return user;
  } catch {
    localStorage.removeItem('kata_user_data');
    return null;
  }
};

export const getAuthTokens = (): AuthTokens | null => {
  if (authTokens) return authTokens;

  // Try localStorage first (for compatibility)
  const stored = localStorage.getItem('kata_auth_tokens');
  if (stored) {
    try {
      const tokens = JSON.parse(stored) as AuthTokens;
      setAuthTokens(tokens);
      return tokens;
    } catch {
      localStorage.removeItem('kata_auth_tokens');
    }
  }

  // Fallback to cookies
  const accessToken = getCookie('kata_access_token');
  const refreshToken = getCookie('kata_refresh_token');

  if (accessToken && refreshToken) {
    const tokens = { access: accessToken, refresh: refreshToken };
    authTokens = tokens;
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
    return tokens;
  }

  return null;
};

// Initialize tokens on startup
getAuthTokens();

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    // Don't add auth token to login requests
    const requestUrl = config.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login/') ||
      requestUrl.endsWith('/auth/login') ||
      requestUrl.indexOf('auth/login') !== -1;

    if (!isLoginRequest) {
      const tokens = getAuthTokens();
      if (tokens && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${tokens.access}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Helper functions for token refresh queue
const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Response interceptor for handling token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Don't handle 401 errors for login requests - let them be handled by the login component
    const requestUrl = originalRequest?.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login/') ||
      requestUrl.endsWith('/auth/login') ||
      requestUrl.indexOf('auth/login') !== -1;

    if (isLoginRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
      (originalRequest as any)._retry = true;

      const tokens = getAuthTokens();

      if (!tokens?.refresh) {
        // No refresh token, redirect to login
        setAuthTokens(null);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      // Start refresh process
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: tokens.refresh,
        });

        const newTokens = {
          access: response.data.access,
          refresh: tokens.refresh,
        };

        setAuthTokens(newTokens);
        isRefreshing = false;

        // Notify all queued requests
        onTokenRefreshed(newTokens.access);

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        isRefreshing = false;
        refreshSubscribers = [];
        setAuthTokens(null);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API error handler
export const handleApiError = (error: AxiosError): string => {
  if (error.response?.data) {
    const data = error.response.data as any;

    // Handle validation errors
    if (typeof data === 'object' && !data.error && !data.detail) {
      const errors = Object.entries(data)
        .map(([field, messages]) => {
          if (Array.isArray(messages)) {
            return `${field}: ${messages.join(', ')}`;
          }
          return `${field}: ${messages}`;
        })
        .join('; ');
      return errors || 'Validation error';
    }

    // Handle standard error responses
    return data.error || data.detail || 'An error occurred';
  }

  if (error.message) {
    return error.message;
  }

  return 'Network error occurred';
};

export default apiClient;
